import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { type CountryConfig, type LanguageConfig, COUNTRIES, LANGUAGES, getCountry, detectCountryFromLocale } from './countries';

interface LocaleContextValue {
  country: CountryConfig;
  language: LanguageConfig;
  availableLanguages: LanguageConfig[];
  setCountry: (countryCode: string) => void;
  setLanguage: (languageCode: string) => void;
}

const LS_COUNTRY_KEY = 'spi-selected-country';

function getInitialCountryCode(): string {
  try {
    const stored = localStorage.getItem(LS_COUNTRY_KEY);
    if (stored && COUNTRIES.some((c) => c.code === stored)) {
      return stored;
    }
  } catch {
    // localStorage unavailable
  }

  // Detect from browser locale
  const browserLocale = navigator.language || 'en';
  return detectCountryFromLocale(browserLocale);
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();

  const [countryCode, setCountryCode] = useState(getInitialCountryCode);

  const country = getCountry(countryCode) || COUNTRIES[0];

  const availableLanguages = country.languages
    .map((code) => LANGUAGES.find((l) => l.code === code))
    .filter((l): l is LanguageConfig => l !== undefined);

  const currentLangCode = i18n.language?.split('-')[0] || 'en';
  const language = LANGUAGES.find((l) => l.code === currentLangCode) || LANGUAGES[0];

  const setCountry = useCallback(
    (code: string) => {
      const newCountry = getCountry(code);
      if (!newCountry) return;

      setCountryCode(code);
      try {
        localStorage.setItem(LS_COUNTRY_KEY, code);
      } catch {
        // localStorage unavailable
      }

      // Switch language to the country's default
      i18n.changeLanguage(newCountry.defaultLanguage);
    },
    [i18n]
  );

  const setLanguage = useCallback(
    (langCode: string) => {
      i18n.changeLanguage(langCode);
    },
    [i18n]
  );

  // On mount, ensure i18n language matches the country's default if no language was explicitly stored
  useEffect(() => {
    const storedLang = localStorage.getItem('spi-language');
    if (!storedLang) {
      i18n.changeLanguage(country.defaultLanguage);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <LocaleContext.Provider
      value={{ country, language, availableLanguages, setCountry, setLanguage }}
    >
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocale(): LocaleContextValue {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }
  return context;
}
